import { HttpEventType, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { StepUpChallengeError } from '@toybank/ui';
import { HttpLogService } from './http-log.service';

/**
 * The status to log for a rejection.
 *
 * `debugHttpInterceptor` is registered OUTSIDE `tbErrorInterceptor`
 * (`withInterceptors([debugHttpInterceptor, tbErrorInterceptor])`), so an in-band step-up 401 has
 * already been translated into a `StepUpChallengeError` by the time it reaches this tap. That
 * error is a plain `Error`, not an `HttpErrorResponse`, so `err.status` is undefined and the log
 * row read **0** — "failed at the network level" to anyone reading the panel, which is exactly how
 * xt run 20260914-065907-payment-order-approvals-0eba mis-diagnosed a step-up block as a
 * CORS/environment fault. Report the 401 it actually was.
 */
function loggedStatus(err: unknown): number {
  if (typeof (err as { status?: unknown } | null)?.status === 'number') {
    return (err as { status: number }).status;
  }
  return err instanceof StepUpChallengeError ? 401 : 0;
}

/**
 * Debug interceptor: records every request/response into the HTTP log panel.
 * Authentication is the normal BFF cookie session installed by /bff/debug/login.
 */
export const debugHttpInterceptor: HttpInterceptorFn = (req, next) => {
  const httpLog = inject(HttpLogService);

  const started = performance.now();
  return next(req).pipe(
    tap({
      next: (event) => {
        if (event.type === HttpEventType.Response) {
          httpLog.add(req.method, req.url, (event as HttpResponse<unknown>).status, performance.now() - started);
        }
      },
      error: (err) => {
        httpLog.add(req.method, req.url, loggedStatus(err), performance.now() - started, true);
      },
    }),
  );
};
